import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import {
  Factory, Wrench, Layers, ArrowLeftRight, ChevronRight, ChevronLeft,
  Check, Building2, Gauge, Ruler, Settings, Warehouse, Clock, GitBranch,
  Calculator, Loader2, CheckCircle2, Circle, Sparkles, BookOpen, CircleDot,
  Play, TestTube, Rocket, CreditCard, Hash, FileText, Users, DollarSign,
  Landmark, Globe, Calendar, Percent, Package, Shield, ArrowRight, SkipForward,
  Lock, ShoppingCart, ClipboardCheck, UserCog, Megaphone, Activity, BarChart3,
  HardHat, Info, Navigation, ClipboardList,
} from "lucide-react";

// ─── Constants ──────────────────────────────────────────────────────────────
const STEPS = [
  { key: "welcome", label: "Welcome", icon: Rocket, required: true },
  { key: "industry", label: "Industry", icon: Factory, required: true },
  { key: "modules", label: "Modules", icon: Package, required: true },
  { key: "company", label: "Company", icon: Building2, required: true },
  { key: "financial", label: "Finance", icon: Calendar, required: true },
  { key: "tax", label: "Tax", icon: Percent, required: true },
  { key: "numbering", label: "Numbering", icon: Hash, required: true },
  { key: "security", label: "Security", icon: Lock, required: false },
  { key: "payment_terms", label: "Payments", icon: CreditCard, required: false },
  { key: "warehouses", label: "Warehouses", icon: Warehouse, required: false },
  { key: "master_data", label: "Materials", icon: Layers, required: false },
  { key: "production", label: "Production", icon: Settings, required: false, mfgOnly: true },
  { key: "users", label: "Users", icon: Users, required: false },
  { key: "review", label: "Launch", icon: CheckCircle2, required: true },
];

const INDUSTRIES = [
  { id: "seamless_pipes", name: "Seamless Pipe Manufacturing", icon: Factory, color: "#C41E3A", desc: "SS/CS seamless pipes, tubes, mother hollows" },
  { id: "fittings", name: "Fittings Manufacturing", icon: Wrench, color: "#2563eb", desc: "Butt-weld fittings, flanges, forged fittings" },
  { id: "plates", name: "Plate Processing", icon: Layers, color: "#7c3aed", desc: "Steel plates, sheets, coils processing" },
  { id: "round_bars", name: "Round Bar Manufacturing", icon: CircleDot, color: "#059669", desc: "Stainless & alloy round bars" },
  { id: "trading", name: "Trading / Distribution", icon: ArrowLeftRight, color: "#d97706", desc: "Steel products trading & distribution" },
];

const MODE_OPTIONS = [
  { id: "testing", label: "Testing / Exploration", icon: TestTube, color: "#2563eb", desc: "Pre-loads sample data to explore features. Can be reset anytime." },
  { id: "production", label: "Production", icon: Rocket, color: "#059669", desc: "Clean database for real operations. Enter your actual business data." },
];

const DEFAULT_DOC_TYPES = [
  { doc_type: "SO", backend_type: "sales_order", label: "Sales Order", prefix: "SO-" },
  { doc_type: "PO", backend_type: "purchase_order", label: "Purchase Order", prefix: "PO-" },
  { doc_type: "INV", backend_type: "invoice", label: "Invoice", prefix: "INV-" },
  { doc_type: "DN", backend_type: "dispatch_note", label: "Dispatch Note", prefix: "DN-" },
  { doc_type: "GRN", backend_type: "grn", label: "Goods Receipt", prefix: "GRN-" },
  { doc_type: "JO", backend_type: "job_order", label: "Job Order", prefix: "JOB-" },
  { doc_type: "BDL", backend_type: "bundle", label: "Bundle", prefix: "BDL-" },
];

// Module definitions for the Modules step
const SETUP_MODULES = [
  { key: "sales", label: "Sales", icon: ShoppingCart, group: "Operations", desc: "Sales Orders, Quotations, Dispatch, CRM" },
  { key: "purchase", label: "Purchase", icon: Package, group: "Operations", desc: "Purchase Orders, GRN, RFQ, Vendors" },
  { key: "production", label: "Production", icon: Factory, group: "Operations", desc: "Job Orders, Production Entries, Planning", mfgOnly: true },
  { key: "inventory", label: "Inventory", icon: Warehouse, group: "Operations", desc: "Stock, Warehouses, Transfers, Packing" },
  { key: "quality", label: "Quality Control", icon: ClipboardCheck, group: "Operations", desc: "QC Inspections, Test Certificates", mfgOnly: true },
  { key: "finance", label: "Finance", icon: Landmark, group: "Finance & Support", desc: "Invoices, Payments, Expenses, Banking" },
  { key: "hr", label: "HR & Payroll", icon: UserCog, group: "Finance & Support", desc: "Employees, Payroll, Leave, Training" },
  { key: "maintenance", label: "Maintenance", icon: Wrench, group: "Finance & Support", desc: "Requests, Schedules, Predictive" },
  { key: "security", label: "Security", icon: Lock, group: "Security & System", desc: "Gate Pass, Visitors, Audit Trail" },
  { key: "approvals", label: "Approvals", icon: Shield, group: "Security & System", desc: "Approval Workflows" },
  { key: "documents", label: "Documents", icon: FileText, group: "Security & System", desc: "Document Management System" },
  { key: "reports", label: "Reports", icon: BarChart3, group: "Security & System", desc: "Reports, Analytics, Scheduled" },
  { key: "announcements", label: "Announcements", icon: Megaphone, group: "Intelligence", desc: "Factory Floor Announcements" },
  { key: "ai_assistant", label: "AI Assistant", icon: Sparkles, group: "Intelligence", desc: "AI Command Center, Smart Analysis" },
  { key: "esign", label: "E-Sign", icon: BookOpen, group: "Advanced", desc: "Digital Signatures" },
  { key: "spreadsheets", label: "Spreadsheets", icon: Activity, group: "Advanced", desc: "Dynamic Spreadsheets" },
  { key: "ai_documents", label: "AI Documents", icon: Sparkles, group: "Advanced", desc: "AI-Generated Documents" },
  { key: "field_tracker", label: "Field Staff Tracker", icon: Navigation, group: "Field Operations", desc: "Tour Plans, GPS Check-ins, Visit Reports" },
  { key: "manual_reporting", label: "Manual Reporting", icon: ClipboardList, group: "Field Operations", desc: "Daily Production, Inventory & Dispatch Reports", mfgOnly: true },
];

// ─── Main Component ─────────────────────────────────────────────────────────
export default function SetupWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Step 0: Welcome
  const [mode, setMode] = useState(null);

  // Step 1: Industry
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [catalog, setCatalog] = useState(null);

  // Step 2: Modules
  const [selectedModules, setSelectedModules] = useState({});
  const [recommendedModules, setRecommendedModules] = useState(null);

  // Step 2: Company
  const [company, setCompany] = useState({
    name: "", gst_number: "", pan_number: "", cin: "",
    address_line1: "", address_line2: "", city: "", state: "", pincode: "", country: "India",
    phone: "", email: "", website: "",
    bank_name: "", bank_account: "", bank_account_name: "", bank_ifsc: "", bank_branch: "",
  });

  // Step 3: Financial
  const [fyStartMonth, setFyStartMonth] = useState(4);
  const [currency, setCurrency] = useState("INR");

  // Step 4: Tax
  const [defaultGstRate, setDefaultGstRate] = useState(18);
  const [enableEinvoice, setEnableEinvoice] = useState(true);
  const [enableEwayBill, setEnableEwayBill] = useState(true);

  // Step 5: Numbering
  const [docPrefixes, setDocPrefixes] = useState(
    DEFAULT_DOC_TYPES.reduce((acc, d) => ({ ...acc, [d.doc_type]: d.prefix }), {})
  );
  const [resetYearly, setResetYearly] = useState(true);

  // Step 6: Payment Terms
  const [paymentTerms, setPaymentTerms] = useState([]);

  // Step 7: Warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseCodes, setSelectedWarehouseCodes] = useState([]);

  // Step 8: Master Data
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);

  // Step 9: Production
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);

  // Step 10: Users (new user to invite)
  const [newUsers, setNewUsers] = useState([]);

  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    max_failed_attempts: 5,
    lockout_duration_minutes: 30,
    alert_on_new_ip: true,
    alert_on_off_hours: true,
  });

  // GST lookup state
  const [gstLoading, setGstLoading] = useState(false);

  const handleGstLookup = async () => {
    const gstin = company.gst_number.trim();
    if (gstin.length !== 15) { toast.error("Enter a valid 15-character GSTIN"); return; }
    setGstLoading(true);
    try {
      const r = await api.get(`/gst/lookup/${gstin}`);
      const d = r.data;
      const decoded = d.decoded || {};
      const updates = {};
      if (d.legal_name || d.trade_name) updates.name = d.legal_name || d.trade_name;
      if (decoded.pan) updates.pan_number = decoded.pan;
      if (d.state || decoded.state) updates.state = d.state || decoded.state;
      if (d.city) updates.city = d.city;
      if (d.pincode) updates.pincode = d.pincode;
      if (d.address_line1) updates.address_line1 = d.address_line1;
      if (d.phone) updates.phone = d.phone;
      if (d.email) updates.email = d.email;

      setCompany(prev => ({ ...prev, ...updates }));

      const src = d.source;
      if (src === "external" || src === "cache" || src === "parties_db") {
        toast.success("Company details fetched successfully");
      } else if (d.external_error) {
        toast.warning(d.external_error, { duration: 8000 });
      } else if (decoded.pan || decoded.state) {
        toast.info("PAN & State auto-filled from GSTIN. Full company details require a GST API key (Settings > Integrations).");
      } else {
        toast.warning("Could not fetch company details. Please enter manually.");
      }
    } catch {
      toast.error("Could not look up GSTIN");
    } finally { setGstLoading(false); }
  };

  const isMfg = selectedIndustry && selectedIndustry !== "trading";
  const activeSteps = STEPS.filter(s => !s.mfgOnly || isMfg);
  const currentStep = activeSteps[step];
  const totalSteps = activeSteps.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  // Load catalog when industry changes
  useEffect(() => {
    if (!selectedIndustry) return;
    api.get(`/setup/catalog/${selectedIndustry}`).then(r => {
      const cat = r.data;
      setCatalog(cat);
      // Pre-select all
      const allSpecs = [];
      (cat.specs?.standard_bodies ? Object.entries(cat.specs.standard_bodies) : []).forEach(([body, specs]) => {
        specs.forEach(s => allSpecs.push({ ...s, standard_body: body }));
      });
      setSelectedSpecs(allSpecs);
      setSelectedGrades((cat.grades?.categories || []).flatMap(c => c.grades));
      setSelectedSizes(cat.sizes?.sizes || []);
      setSelectedMachines(cat.machines || []);
      setSelectedStages(cat.production_stages || []);
      setWarehouses(cat.warehouses || []);
      // Pre-tick only real stock warehouses (Transit/Returns are optional virtual locations)
      setSelectedWarehouseCodes((cat.warehouses || []).filter(w => (w.type || "Stock").toLowerCase() === "stock").map(w => w.code));
      // Default shifts - catalog returns shifts as a dict keyed by pattern id
      const shifts = cat.shifts?.["3_shift"] || cat.shift_patterns?.find?.(p => p.id === "3_shift")?.shifts || [];
      setSelectedShifts(shifts);
    }).catch(() => {});

    // Load recommended modules for the industry (guard against stale responses
    // when the user switches industry quickly — last selection must win)
    const industryAtFetch = selectedIndustry;
    api.get(`/setup/recommended-modules/${selectedIndustry}`).then(r => {
      if (industryAtFetch !== selectedIndustry) return;
      setRecommendedModules(r.data);
      const modMap = {};
      [...(r.data.core || []), ...(r.data.recommended || [])].forEach(k => { modMap[k] = true; });
      (r.data.optional || []).forEach(k => { modMap[k] = false; });
      setSelectedModules(modMap);
    }).catch(() => {});
  }, [selectedIndustry]);

  // Load payment terms defaults
  useEffect(() => {
    api.get("/payment-terms").then(r => setPaymentTerms(r.data || [])).catch(() => {});
  }, []);

  // Load existing company profile
  useEffect(() => {
    api.get("/company-profile").then(r => {
      if (r.data && r.data.name) {
        setCompany(prev => ({ ...prev, ...r.data }));
      }
    }).catch(() => {});
    api.get("/settings/system").then(r => {
      if (r.data) {
        setFyStartMonth(r.data.fy_start_month || 4);
        setCurrency(r.data.currency || "INR");
      }
    }).catch(() => {});
  }, []);

  const canProceed = () => {
    if (!currentStep) return false;
    switch (currentStep.key) {
      case "welcome": return !!mode;
      case "industry": return !!selectedIndustry;
      case "modules": return Object.values(selectedModules).some(v => v === true);
      case "company": return company.name.trim() && company.gst_number.trim();
      case "financial": return !!fyStartMonth && !!currency;
      case "tax": return defaultGstRate >= 0;
      case "numbering": return Object.values(docPrefixes).every(p => p.trim());
      default: return true;
    }
  };

  const saveStepData = async () => {
    setSaving(true);
    try {
      switch (currentStep.key) {
        case "modules":
          await api.post("/setup/save-modules", { enabled: selectedModules });
          break;
        case "company":
          await api.put("/company-profile", company);
          break;
        case "financial":
          await api.put("/settings/system", {
            category: "system", fy_start_month: fyStartMonth, currency, date_format: "DD-MMM-YYYY", timezone: "Asia/Kolkata", language: "en",
          });
          break;
        case "tax":
          await api.put("/settings/sales", {
            category: "sales", default_gst_rate: defaultGstRate, enable_einvoice: enableEinvoice, enable_eway_bill: enableEwayBill,
          });
          break;
        case "numbering":
          for (const dt of DEFAULT_DOC_TYPES) {
            await api.put(`/doc-sequences/${dt.backend_type}`, {
              prefix: docPrefixes[dt.doc_type], separator: "-", padding: 4, reset_yearly: resetYearly,
              year_format: resetYearly ? "FY" : "",
            });
          }
          break;
        case "security":
          await api.put("/security-intel/rules", securitySettings);
          break;
        default: break;
      }
    } catch (err) {
      console.error("Save step error:", err);
      toast.error(`Could not save this step (${err.response?.data?.detail || err.message}). Please retry.`);
      setSaving(false);
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep.required && !canProceed()) {
      toast.error("Please complete all required fields");
      return;
    }
    const ok = await saveStepData();
    if (!ok) return;
    if (step < totalSteps - 1) setStep(step + 1);
  };

  const handleSkip = () => {
    if (step < totalSteps - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleLaunch = async () => {
    setApplying(true);
    try {
      // Save module configuration (permanent)
      await api.post("/setup/save-modules", { enabled: selectedModules });
      // Apply all config via the setup endpoint
      await api.post("/setup/apply-config", {
        industry_type: selectedIndustry,
        mode,
        specs: selectedSpecs,
        grades: selectedGrades,
        sizes: selectedSizes,
        machines: selectedMachines,
        warehouses: warehouses.filter(w => selectedWarehouseCodes.includes(w.code)),
        shifts: selectedShifts,
        production_stages: selectedStages,
        weight_formula: catalog?.weight_formula || {},
      });
      // Register new users
      for (const u of newUsers) {
        if (u.email && u.full_name) {
          try { await api.post("/auth/register", u); } catch {} // ignore dups
        }
      }
      await api.post("/setup/complete", { industry_type: selectedIndustry });
      toast.success("Setup complete! Review your modules before inviting the team...");
      setTimeout(() => navigate("/settings?tab=modules&fresh=1", { replace: true }), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setApplying(false);
    }
  };

  // ─── Step Renderers ─────────────────────────────────────────────────────
  const renderWelcome = () => (
    <div className="space-y-8">
      <div className="text-center max-w-lg mx-auto">
        <h2 className="text-2xl font-bold">Welcome to SteelOne ERP</h2>
        <p className="text-muted-foreground mt-2 text-sm">Let's set up your system. This wizard will guide you through all the essential configuration.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {MODE_OPTIONS.map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
            className={cn("text-left p-6 rounded-xl border-2 transition-all", mode === m.id ? "border-primary bg-primary/5 shadow-lg" : "border-border hover:border-border/80")}>
            <m.icon className="w-8 h-8 mb-3" style={{ color: m.color }} />
            <h3 className="font-semibold">{m.label}</h3>
            <p className="text-xs text-muted-foreground mt-1">{m.desc}</p>
            {mode === m.id && <div className="flex items-center gap-1.5 mt-3 text-xs text-primary font-medium"><Check className="w-3.5 h-3.5" /> Selected</div>}
          </button>
        ))}
      </div>
    </div>
  );

  const renderIndustry = () => (
    <div className="space-y-6">
      <div className="text-center"><h2 className="text-xl font-bold">Select Your Industry</h2><p className="text-sm text-muted-foreground mt-1">This determines which modules and master data are configured</p></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {INDUSTRIES.map(ind => (
          <button key={ind.id} onClick={() => setSelectedIndustry(ind.id)} data-testid={`industry-${ind.id}`}
            className={cn("text-left p-5 rounded-xl border-2 transition-all", selectedIndustry === ind.id ? "border-primary bg-primary/5 shadow-lg" : "border-border hover:border-border/80")}>
            <ind.icon className="w-7 h-7 mb-2" style={{ color: ind.color }} />
            <h3 className="font-semibold text-sm">{ind.name}</h3>
            <p className="text-xs text-muted-foreground mt-1">{ind.desc}</p>
            {selectedIndustry === ind.id && <div className="mt-2"><Check className="w-4 h-4 text-primary" /></div>}
          </button>
        ))}
      </div>
    </div>
  );

  const renderModules = () => {
    const enabledCount = Object.values(selectedModules).filter(Boolean).length;
    const groups = {};
    SETUP_MODULES.forEach(m => {
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group].push(m);
    });

    const toggleModule = (key) => {
      if (recommendedModules?.core?.includes(key)) return;
      setSelectedModules(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const resetToRecommended = () => {
      if (!recommendedModules) return;
      const modMap = {};
      [...(recommendedModules.core || []), ...(recommendedModules.recommended || [])].forEach(k => { modMap[k] = true; });
      (recommendedModules.optional || []).forEach(k => { modMap[k] = false; });
      setSelectedModules(modMap);
      toast.info("Reset to industry recommendations");
    };

    return (
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="step-modules">
        <div className="text-center">
          <h2 className="text-xl font-bold">Choose Your Modules</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Based on your industry, we've pre-selected the recommended modules. You can customize below.
          </p>
          <p className="text-xs text-muted-foreground mt-1 font-medium">
            {enabledCount} modules selected
          </p>
        </div>

        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-blue-500/10 text-blue-700 px-3 py-2 rounded-lg">
            <Info className="w-3.5 h-3.5 shrink-0" />
            <span>🔒 <strong>Core</strong> modules are essential for your industry · ✅ <strong>Recommended</strong> are pre-selected · you can change modules anytime later in Settings → Modules.</span>
          </div>
          <Button variant="ghost" size="sm" onClick={resetToRecommended} className="text-xs gap-1.5" data-testid="reset-modules-btn">
            <ArrowRight className="w-3 h-3" /> Reset to Recommended
          </Button>
        </div>

        {Object.entries(groups).map(([groupName, groupModules]) => (
          <div key={groupName} className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider px-1">{groupName}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupModules.map(m => {
                const Icon = m.icon;
                const isOn = selectedModules[m.key] === true;
                const isCore = recommendedModules?.core?.includes(m.key);
                const isRecommended = recommendedModules?.recommended?.includes(m.key);
                return (
                  <button key={m.key} onClick={() => toggleModule(m.key)}
                    className={cn(
                      "relative text-left p-4 rounded-xl border-2 transition-all group",
                      isOn ? "border-primary/40 bg-primary/5" : "border-border/50 opacity-50 hover:opacity-80",
                      isCore && "cursor-default border-primary/60"
                    )}
                    data-testid={`setup-module-${m.key}`}>
                    {isCore ? (
                      <span className="absolute -top-2 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid={`core-badge-${m.key}`}>
                        🔒 Core
                      </span>
                    ) : isRecommended ? (
                      <span className="absolute -top-2 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 border border-green-500/20">
                        Recommended
                      </span>
                    ) : (
                      <span className="absolute -top-2 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                        Optional
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", isOn ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{m.label}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{m.desc}</p>
                      </div>
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all",
                        isOn ? "border-primary bg-primary" : "border-muted-foreground/30"
                      )}>
                        {isOn && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompany = () => (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Company Profile</h2><p className="text-sm text-muted-foreground mt-1">This information appears on invoices, POs, and all documents</p></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><Label>Company Name <span className="text-destructive">*</span></Label><Input value={company.name} onChange={e => setCompany(p => ({ ...p, name: e.target.value }))} placeholder="Your Company Pvt. Ltd." data-testid="company-name" /></div>
        <div className="md:col-span-2">
          <Label>GST Number <span className="text-destructive">*</span></Label>
          <div className="flex gap-2">
            <Input className="flex-1" value={company.gst_number} onChange={e => setCompany(p => ({ ...p, gst_number: e.target.value.toUpperCase() }))} placeholder="24AABCS1234F1Z5" data-testid="company-gst" />
            <Button variant="outline" size="sm" disabled={gstLoading || company.gst_number.length !== 15} onClick={handleGstLookup} data-testid="gst-lookup-btn" className="shrink-0 whitespace-nowrap">
              {gstLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              <span className="ml-1.5">Fetch Details</span>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Enter 15-digit GSTIN and click Fetch to auto-fill company details</p>
        </div>
        <div><Label>PAN Number</Label><Input value={company.pan_number} onChange={e => setCompany(p => ({ ...p, pan_number: e.target.value.toUpperCase() }))} placeholder="AABCS1234F" /></div>
        <div><Label>CIN</Label><Input value={company.cin} onChange={e => setCompany(p => ({ ...p, cin: e.target.value }))} placeholder="U12345MH2000PTC123456" /></div>
        <div><Label>Phone</Label><Input value={company.phone} onChange={e => setCompany(p => ({ ...p, phone: e.target.value }))} placeholder="+91 22 12345678" /></div>
        <div><Label>Email</Label><Input value={company.email} onChange={e => setCompany(p => ({ ...p, email: e.target.value }))} placeholder="info@company.com" /></div>
        <div><Label>Website</Label><Input value={company.website} onChange={e => setCompany(p => ({ ...p, website: e.target.value }))} placeholder="www.company.com" /></div>
        <div className="md:col-span-2"><Label>Address Line 1</Label><Input value={company.address_line1} onChange={e => setCompany(p => ({ ...p, address_line1: e.target.value }))} placeholder="Plot No, Street Name" /></div>
        <div><Label>City</Label><Input value={company.city} onChange={e => setCompany(p => ({ ...p, city: e.target.value }))} placeholder="Mumbai" /></div>
        <div><Label>State</Label><Input value={company.state} onChange={e => setCompany(p => ({ ...p, state: e.target.value }))} placeholder="Maharashtra" /></div>
        <div><Label>Pincode</Label><Input value={company.pincode} onChange={e => setCompany(p => ({ ...p, pincode: e.target.value }))} placeholder="400001" /></div>
      </div>
      <div className="border-t pt-5">
        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Landmark className="w-4 h-4" /> Primary Bank Details <span className="text-xs text-muted-foreground font-normal">(printed on invoices)</span></h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Bank Name</Label><Input value={company.bank_name} onChange={e => setCompany(p => ({ ...p, bank_name: e.target.value }))} placeholder="ICICI Bank" /></div>
          <div><Label>Account Number</Label><Input value={company.bank_account} onChange={e => setCompany(p => ({ ...p, bank_account: e.target.value }))} placeholder="0123456789012" /></div>
          <div><Label>Account Holder Name</Label><Input value={company.bank_account_name} onChange={e => setCompany(p => ({ ...p, bank_account_name: e.target.value }))} placeholder="Company Pvt Ltd" /></div>
          <div><Label>IFSC Code</Label><Input value={company.bank_ifsc} onChange={e => setCompany(p => ({ ...p, bank_ifsc: e.target.value.toUpperCase() }))} placeholder="ICIC0001234" /></div>
          <div><Label>Branch</Label><Input value={company.bank_branch} onChange={e => setCompany(p => ({ ...p, bank_branch: e.target.value }))} placeholder="Andheri East" /></div>
        </div>
      </div>
    </div>
  );

  const renderFinancial = () => (
    <div className="space-y-8 max-w-xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Financial Year & Currency</h2><p className="text-sm text-muted-foreground mt-1">Configure your fiscal year and default currency</p></div>
      <div className="space-y-6">
        <div>
          <Label>Financial Year Start Month</Label>
          <select className="w-full h-10 mt-1 rounded-md border bg-background px-3 text-sm" value={fyStartMonth} onChange={e => setFyStartMonth(Number(e.target.value))} data-testid="fy-month">
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m,i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">In India, the financial year starts in April (month 4)</p>
        </div>
        <div>
          <Label>Default Currency</Label>
          <select className="w-full h-10 mt-1 rounded-md border bg-background px-3 text-sm" value={currency} onChange={e => setCurrency(e.target.value)} data-testid="currency">
            <option value="INR">INR - Indian Rupee</option>
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="AED">AED - UAE Dirham</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderTax = () => (
    <div className="space-y-8 max-w-xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Tax Configuration</h2><p className="text-sm text-muted-foreground mt-1">Default GST rate applied to new line items</p></div>
      <div className="space-y-6">
        <div>
          <Label>Default GST Rate (%)</Label>
          <div className="flex gap-3 mt-2">
            {[0, 5, 12, 18, 28].map(rate => (
              <button key={rate} onClick={() => setDefaultGstRate(rate)} data-testid={`gst-${rate}`}
                className={cn("px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all", defaultGstRate === rate ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30")}>
                {rate}%
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Steel products typically attract 18% GST</p>
        </div>
        <div className="flex items-center justify-between p-4 rounded-lg border">
          <div><p className="text-sm font-medium">E-Invoice</p><p className="text-xs text-muted-foreground">Auto-generate IRN via government portal</p></div>
          <Checkbox checked={enableEinvoice} onCheckedChange={setEnableEinvoice} data-testid="einvoice-toggle" />
        </div>
        <div className="flex items-center justify-between p-4 rounded-lg border">
          <div><p className="text-sm font-medium">E-Way Bill</p><p className="text-xs text-muted-foreground">Generate e-way bills for dispatches</p></div>
          <Checkbox checked={enableEwayBill} onCheckedChange={setEnableEwayBill} data-testid="eway-toggle" />
        </div>
      </div>
    </div>
  );

  const renderNumbering = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Document Numbering</h2><p className="text-sm text-muted-foreground mt-1">Set prefixes for auto-generated document numbers</p></div>
      <div className="space-y-3">
        {DEFAULT_DOC_TYPES.map(dt => (
          <div key={dt.doc_type} className="flex items-center gap-4 p-3 rounded-lg border" data-testid={`doc-${dt.doc_type}`}>
            <div className="w-28 shrink-0"><span className="text-sm font-medium">{dt.label}</span></div>
            <Input value={docPrefixes[dt.doc_type]} onChange={e => setDocPrefixes(p => ({ ...p, [dt.doc_type]: e.target.value }))} className="w-32" placeholder={dt.prefix} />
            <span className="text-sm text-muted-foreground">Preview: <span className="font-mono font-semibold">{docPrefixes[dt.doc_type]}0001</span></span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between p-4 rounded-lg border">
        <div><p className="text-sm font-medium">Reset Yearly</p><p className="text-xs text-muted-foreground">Restart numbering at the beginning of each financial year</p></div>
        <Checkbox checked={resetYearly} onCheckedChange={setResetYearly} data-testid="reset-yearly" />
      </div>
    </div>
  );

  const renderPaymentTerms = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Payment Terms</h2><p className="text-sm text-muted-foreground mt-1">Default payment terms used in sales & purchase orders. You can customize these later.</p></div>
      {paymentTerms.length > 0 ? (
        <div className="space-y-2">
          {paymentTerms.map((pt, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              <div>
                <p className="text-sm font-medium">{pt.label}</p>
                <p className="text-xs text-muted-foreground">{pt.description}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-8 text-muted-foreground text-sm">Default payment terms will be created automatically</div>
      )}
    </div>
  );

  const renderWarehouses = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Warehouses & Locations</h2><p className="text-sm text-muted-foreground mt-1">Storage locations for inventory management</p></div>
      {warehouses.length > 0 ? (
        <div className="space-y-2">
          {warehouses.map((wh, i) => {
            const isVirtual = (wh.type || "Stock").toLowerCase() !== "stock";
            const checked = selectedWarehouseCodes.includes(wh.code);
            const toggle = () => setSelectedWarehouseCodes(prev => checked ? prev.filter(c => c !== wh.code) : [...prev, wh.code]);
            return (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/40" onClick={toggle} data-testid={`wizard-warehouse-${wh.code}`}>
                <Checkbox checked={checked} onCheckedChange={toggle} onClick={e => e.stopPropagation()} data-testid={`wizard-warehouse-check-${wh.code}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{wh.name}</p>
                  <p className="text-xs text-muted-foreground">{wh.code} - {wh.type || "General"}{isVirtual ? " · optional virtual location (not a physical godown)" : ""}</p>
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground pt-1">Only ticked locations will be created. You can add more anytime in Master Data → Warehouses.</p>
        </div>
      ) : (
        <div className="text-center p-8 text-muted-foreground text-sm">Default warehouses will be created based on your industry selection</div>
      )}
    </div>
  );

  const renderMasterData = () => {
    const specCount = selectedSpecs.length;
    const gradeCount = selectedGrades.length;
    const sizeCount = selectedSizes.length;
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="text-center"><h2 className="text-xl font-bold">Master Data</h2><p className="text-sm text-muted-foreground mt-1">Material specifications, grades, and sizes pre-loaded from your industry catalog</p></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-xl border text-center">
            <p className="text-3xl font-bold text-primary">{specCount}</p>
            <p className="text-xs text-muted-foreground mt-1">Specifications</p>
          </div>
          <div className="p-4 rounded-xl border text-center">
            <p className="text-3xl font-bold text-primary">{gradeCount}</p>
            <p className="text-xs text-muted-foreground mt-1">Grades</p>
          </div>
          <div className="p-4 rounded-xl border text-center">
            <p className="text-3xl font-bold text-primary">{sizeCount}</p>
            <p className="text-xs text-muted-foreground mt-1">Pipe Sizes</p>
          </div>
        </div>
        <p className="text-xs text-center text-muted-foreground">All items are pre-selected from the {INDUSTRIES.find(i => i.id === selectedIndustry)?.name || ""} catalog. You can add/remove items later in Masters settings.</p>
      </div>
    );
  };

  const renderProduction = () => (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Machinery & Production</h2><p className="text-sm text-muted-foreground mt-1">Machines, shifts, and production stages for your facility</p></div>
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border text-center">
          <Settings className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{selectedMachines.length}</p>
          <p className="text-xs text-muted-foreground">Machines</p>
        </div>
        <div className="p-4 rounded-xl border text-center">
          <Clock className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{selectedShifts.length}</p>
          <p className="text-xs text-muted-foreground">Shifts</p>
        </div>
        <div className="p-4 rounded-xl border text-center">
          <GitBranch className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{selectedStages.length}</p>
          <p className="text-xs text-muted-foreground">Production Stages</p>
        </div>
      </div>
      <p className="text-xs text-center text-muted-foreground">Pre-loaded from your industry catalog. Customize in Settings &gt; Operations later.</p>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center"><h2 className="text-xl font-bold">Invite Team Members</h2><p className="text-sm text-muted-foreground mt-1">Add users who will access the ERP. You can always add more later.</p></div>
      {newUsers.map((u, i) => (
        <div key={i} className="flex gap-3 items-end p-3 rounded-lg border">
          <div className="flex-1"><Label className="text-xs">Name</Label><Input value={u.full_name} onChange={e => { const arr = [...newUsers]; arr[i].full_name = e.target.value; setNewUsers(arr); }} placeholder="Full Name" /></div>
          <div className="flex-1"><Label className="text-xs">Email</Label><Input value={u.email} onChange={e => { const arr = [...newUsers]; arr[i].email = e.target.value; setNewUsers(arr); }} placeholder="email@company.com" /></div>
          <Button variant="ghost" size="sm" className="text-destructive shrink-0" onClick={() => setNewUsers(newUsers.filter((_, j) => j !== i))}>Remove</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setNewUsers([...newUsers, { full_name: "", email: "", password: "Welcome@123", role_id: "" }])} data-testid="add-user-btn">
        <Users className="w-3.5 h-3.5" /> Add User
      </Button>
      {newUsers.length > 0 && <p className="text-xs text-muted-foreground">Default password: <span className="font-mono">Welcome@123</span> — users will be prompted to change on first login.</p>}
    </div>
  );

  const renderReview = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4"><Rocket className="w-8 h-8 text-green-600" /></div>
        <h2 className="text-xl font-bold">Ready to Launch</h2>
        <p className="text-sm text-muted-foreground mt-1">Review your configuration and launch the system</p>
      </div>
      <div className="space-y-2">
        {[
          ["Mode", mode === "testing" ? "Testing / Exploration" : "Production"],
          ["Industry", INDUSTRIES.find(i => i.id === selectedIndustry)?.name || ""],
          ["Modules", `${Object.values(selectedModules).filter(Boolean).length} active modules`],
          ["Company", company.name],
          ["GST", company.gst_number],
          ["Financial Year", `Starts ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][fyStartMonth-1]}`],
          ["Currency", currency],
          ["Default GST", `${defaultGstRate}%`],
          ["Grades", `${selectedGrades.length} grades`],
          ["Sizes", `${selectedSizes.length} sizes`],
          isMfg ? ["Machines", `${selectedMachines.length} machines`] : null,
          ["Lockout", `${securitySettings.max_failed_attempts} attempts / ${securitySettings.lockout_duration_minutes} min`],
          newUsers.length > 0 ? ["New Users", `${newUsers.length} invited`] : null,
        ].filter(Boolean).map(([label, value], i) => (
          <div key={i} className="flex justify-between p-3 rounded-lg border text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center">
        <h2 className="text-xl font-bold">Security Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure login security and account lockout policies</p>
      </div>

      <div className="space-y-5">
        <div className="p-5 rounded-xl border space-y-4">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> Account Lockout</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Max Failed Login Attempts</Label>
              <Input type="number" min={3} max={20} value={securitySettings.max_failed_attempts}
                onChange={e => setSecuritySettings(p => ({ ...p, max_failed_attempts: parseInt(e.target.value) || 5 }))}
                data-testid="security-max-attempts" />
              <p className="text-xs text-muted-foreground mt-1">Account locks after this many failed attempts</p>
            </div>
            <div>
              <Label>Lockout Duration (minutes)</Label>
              <Input type="number" min={5} max={120} value={securitySettings.lockout_duration_minutes}
                onChange={e => setSecuritySettings(p => ({ ...p, lockout_duration_minutes: parseInt(e.target.value) || 30 }))}
                data-testid="security-lockout-duration" />
              <p className="text-xs text-muted-foreground mt-1">How long the account stays locked</p>
            </div>
          </div>
        </div>

        <div className="p-5 rounded-xl border space-y-4">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> Alerts</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="security-alert-new-ip">
              <Checkbox checked={securitySettings.alert_on_new_ip}
                onCheckedChange={v => setSecuritySettings(p => ({ ...p, alert_on_new_ip: !!v }))} />
              <div>
                <p className="text-sm font-medium">Alert on new IP address</p>
                <p className="text-xs text-muted-foreground">Get notified when login from unrecognized IP</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="security-alert-off-hours">
              <Checkbox checked={securitySettings.alert_on_off_hours}
                onCheckedChange={v => setSecuritySettings(p => ({ ...p, alert_on_off_hours: !!v }))} />
              <div>
                <p className="text-sm font-medium">Alert on off-hours login</p>
                <p className="text-xs text-muted-foreground">Get notified for logins outside business hours (10 PM - 6 AM)</p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const stepRenderers = {
    welcome: renderWelcome, industry: renderIndustry, modules: renderModules, company: renderCompany,
    financial: renderFinancial, tax: renderTax, numbering: renderNumbering,
    security: renderSecurity, payment_terms: renderPaymentTerms, warehouses: renderWarehouses,
    master_data: renderMasterData, production: renderProduction,
    users: renderUsers, review: renderReview,
  };

  // ─── Layout ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex" data-testid="unified-setup-wizard">
      {/* Sidebar stepper */}
      <div className="hidden lg:flex w-72 bg-[#1a1a1f] flex-col p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-md bg-[#C41E3A] flex items-center justify-center text-white font-bold text-sm">ST</div>
          <span className="text-white font-bold tracking-tight">SteelOne ERP</span>
        </div>
        <div className="space-y-1 flex-1">
          {activeSteps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button key={s.key} onClick={() => i < step && setStep(i)} disabled={i > step}
                className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left",
                  isActive ? "bg-white/10 text-white" : isDone ? "text-white/70 hover:bg-white/5 cursor-pointer" : "text-white/30 cursor-not-allowed")}>
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0",
                  isActive ? "bg-[#C41E3A] text-white" : isDone ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/40")}>
                  {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <span>{s.label}</span>
                {!s.required && <span className="ml-auto text-[10px] text-white/30">optional</span>}
              </button>
            );
          })}
        </div>
        <div className="mt-auto">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#C41E3A] transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} /></div>
          <p className="text-white/40 text-xs mt-2">{progress}% complete</p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 p-4 border-b">
          <div className="w-8 h-8 rounded-md bg-[#C41E3A] flex items-center justify-center text-white font-bold text-xs">ST</div>
          <span className="font-bold text-sm">Step {step + 1} of {totalSteps}</span>
          <div className="ml-auto h-1.5 w-24 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary transition-all rounded-full" style={{ width: `${progress}%` }} /></div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-10">
          {currentStep && stepRenderers[currentStep.key]?.()}
        </div>

        {/* Footer navigation */}
        <div className="border-t p-4 flex items-center gap-3">
          {step > 0 && (
            <Button variant="outline" onClick={handleBack} className="gap-1.5" data-testid="setup-back-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
          )}
          <div className="flex-1" />
          {currentStep && !currentStep.required && currentStep.key !== "review" && (
            <Button variant="ghost" onClick={handleSkip} className="gap-1.5 text-muted-foreground" data-testid="setup-skip-btn">
              Skip <SkipForward className="w-4 h-4" />
            </Button>
          )}
          {currentStep?.key === "review" ? (
            <Button onClick={handleLaunch} disabled={applying} className="gap-1.5 bg-green-600 hover:bg-green-700" data-testid="setup-launch-btn">
              {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying...</> : <><Rocket className="w-4 h-4" /> Apply & Launch</>}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={saving || !canProceed()} className="gap-1.5" data-testid="setup-next-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Next <ChevronRight className="w-4 h-4" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
